import React, { useState, useEffect } from 'react';
import {
  Text,
  Button,
  Checkbox,
  makeStyles,
  shorthands,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import {
  ArrowLeft20Regular,
  Checkmark20Regular,
} from '@fluentui/react-icons';
import { policyService } from '../../services/policyService';
import { ApprovedDomain, DomainAssignment, PolicyOption } from './types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  header: {
    marginBottom: '8px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  matrixContainer: {
    overflowX: 'auto',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '600px',
  },
  headerRow: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  headerCell: {
    ...shorthands.padding('12px'),
    textAlign: 'left',
    fontWeight: '600',
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderBottom('2px', 'solid', tokens.colorNeutralStroke1),
  },
  policyHeaderCell: {
    ...shorthands.padding('12px'),
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderBottom('2px', 'solid', tokens.colorNeutralStroke1),
  },
  row: {
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  cell: {
    ...shorthands.padding('12px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  domainCell: {
    fontFamily: 'monospace',
    fontSize: '13px',
    fontWeight: '600',
  },
  organizationText: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    display: 'block',
    marginTop: '2px',
  },
  checkboxCell: {
    textAlign: 'center',
  },
  selectAllCell: {
    textAlign: 'center',
    ...shorthands.padding('12px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  selectAllHeaderCell: {
    ...shorthands.padding('12px'),
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderBottom('2px', 'solid', tokens.colorNeutralStroke1),
    minWidth: '120px',
  },
  selectAllCheckboxContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.gap('4px'),
    marginTop: '8px',
  },
  selectAllLabel: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  helperText: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
  },
  summaryText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    ...shorthands.padding('16px', '0', '0', '0'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('40px'),
  },
});

interface MatrixAssignmentProps {
  domains: ApprovedDomain[];
  onBack: () => void;
  onComplete: (assignments: DomainAssignment[]) => void;
}

export const MatrixAssignment: React.FC<MatrixAssignmentProps> = ({
  domains,
  onBack,
  onComplete,
}) => {
  const styles = useStyles();
  const [policies, setPolicies] = useState<PolicyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selections, setSelections] = useState<Map<string, Set<string>>>(new Map());

  useEffect(() => {
    loadPolicies();
    // Initialize selections map
    const initialSelections = new Map<string, Set<string>>();
    domains.forEach(domain => {
      initialSelections.set(domain.id, new Set());
    });
    setSelections(initialSelections);
  }, [domains]);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const allPolicies = await policyService.getPolicies();
      
      // Filter to only managed policies (those ending with _OD) 
      // and exclude policies that are being deleted
      const managedPolicies = allPolicies.filter(p => {
        const isManaged = p.name.endsWith('_OD');
        const isDeletingPolicy = (p as any).deletionStatus === 'pending' || (p as any).deletionStatus === 'in_progress';
        return isManaged && !isDeletingPolicy;
      });
      
      const options: PolicyOption[] = managedPolicies.map(p => ({
        id: p.id,
        name: p.name,
        domainCount: 0, // Not displaying domain count
        userGroup: p.userGroup,
      }));
      setPolicies(options);
    } catch (err) {
      console.error('Failed to load policies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (domainId: string, policyId: string, checked: boolean) => {
    const newSelections = new Map(selections);
    const domainPolicies = new Set<string>(newSelections.get(domainId) || new Set<string>());
    
    if (checked) {
      domainPolicies.add(policyId);
    } else {
      domainPolicies.delete(policyId);
    }
    
    newSelections.set(domainId, domainPolicies);
    setSelections(newSelections);
  };

  // Select all domains for a specific policy
  const handleSelectAllDomainsForPolicy = (policyId: string, checked: boolean) => {
    const newSelections = new Map(selections);
    
    domains.forEach(domain => {
      const domainPolicies = new Set<string>(newSelections.get(domain.id) || new Set<string>());
      
      if (checked) {
        domainPolicies.add(policyId);
      } else {
        domainPolicies.delete(policyId);
      }
      
      newSelections.set(domain.id, domainPolicies);
    });
    
    setSelections(newSelections);
  };

  // Select all policies for a specific domain
  const handleSelectAllPoliciesForDomain = (domainId: string, checked: boolean) => {
    const newSelections = new Map(selections);
    const domainPolicies = new Set<string>(newSelections.get(domainId) || new Set<string>());
    
    if (checked) {
      // Add all policy IDs
      policies.forEach(policy => domainPolicies.add(policy.id));
    } else {
      // Clear all policy IDs
      domainPolicies.clear();
    }
    
    newSelections.set(domainId, domainPolicies);
    setSelections(newSelections);
  };

  // Check if all domains are selected for a policy
  const areAllDomainsSelectedForPolicy = (policyId: string): boolean => {
    return domains.every(domain => selections.get(domain.id)?.has(policyId));
  };

  // Check if all policies are selected for a domain
  const areAllPoliciesSelectedForDomain = (domainId: string): boolean => {
    const domainPolicies = selections.get(domainId);
    if (!domainPolicies) return false;
    return policies.every(policy => domainPolicies.has(policy.id));
  };

  const handleComplete = () => {
    const assignments: DomainAssignment[] = domains.map(domain => {
      const policyIds = Array.from(selections.get(domain.id) || new Set<string>()) as string[];
      return {
        domainId: domain.id,
        domainName: domain.domain,
        policyIds,
        status: policyIds.length > 0 ? 'pending' : 'skipped',
      };
    });
    onComplete(assignments);
  };

  // Calculate summary
  const totalAssignments = Array.from(selections.values()).reduce(
    (sum, policySet) => sum + policySet.size,
    0
  );
  const domainsWithAssignments = Array.from(selections.values()).filter(
    policySet => policySet.size > 0
  ).length;

  if (loading) {
    return (
      <div className={styles.loadingContainer} data-testid="matrix-assignment-loading">
        <Spinner size="large" label="Loading policies..." data-testid="matrix-assignment-loading-spinner" />
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="matrix-assignment">
      <div className={styles.helperText} data-testid="matrix-assignment-helper-text-container">
        <Text data-testid="matrix-assignment-helper-text">
          Use the checkboxes to select which domains are added to each policy’s allowed domains list. 
        </Text>
      </div>

      <div className={styles.matrixContainer} data-testid="matrix-assignment-table-container">
        <table className={styles.table} data-testid="matrix-assignment-table">
          <thead data-testid="matrix-assignment-table-head">
            <tr className={styles.headerRow} data-testid="matrix-assignment-header-row">
              <th className={styles.headerCell} data-testid="matrix-assignment-header-domain">Domain</th>
              {policies.map((policy, index) => (
                <th key={policy.id} className={styles.policyHeaderCell} data-testid={`matrix-assignment-policy-header-${index}`}>
                  <div data-testid={`matrix-assignment-policy-header-name-${index}`}>
                    {policy.name}
                  </div>
                  <div className={styles.selectAllCheckboxContainer} data-testid={`matrix-assignment-policy-select-all-container-${index}`}>
                    <Checkbox
                      checked={areAllDomainsSelectedForPolicy(policy.id)}
                      onChange={(_, data) =>
                        handleSelectAllDomainsForPolicy(policy.id, data.checked === true)
                      }
                      data-testid={`matrix-assignment-policy-select-all-checkbox-${index}`}
                    />
                  </div>
                </th>
              ))}
              <th className={styles.selectAllHeaderCell} data-testid="matrix-assignment-header-select-all-policies">
                Select All Policies
              </th>
            </tr>
          </thead>
          <tbody data-testid="matrix-assignment-table-body">
            {domains.map((domain, domainIndex) => (
              <tr key={domain.id} className={styles.row} data-testid={`matrix-assignment-row-${domainIndex}`}>
                <td className={`${styles.cell} ${styles.domainCell}`} data-testid={`matrix-assignment-domain-cell-${domainIndex}`}>
                  {domain.domain}
                  {domain.organization && (
                    <span className={styles.organizationText} data-testid={`matrix-assignment-domain-organization-${domainIndex}`}>{domain.organization}</span>
                  )}
                </td>
                {policies.map((policy, policyIndex) => (
                  <td key={policy.id} className={`${styles.cell} ${styles.checkboxCell}`} data-testid={`matrix-assignment-policy-cell-${domainIndex}-${policyIndex}`}>
                    <Checkbox
                      checked={selections.get(domain.id)?.has(policy.id) || false}
                      onChange={(_, data) =>
                        handleCheckboxChange(domain.id, policy.id, data.checked === true)
                      }
                      data-testid={`matrix-assignment-checkbox-${domainIndex}-${policyIndex}`}
                    />
                  </td>
                ))}
                <td className={`${styles.cell} ${styles.selectAllCell}`} data-testid={`matrix-assignment-domain-select-all-cell-${domainIndex}`}>
                  <Checkbox
                    checked={areAllPoliciesSelectedForDomain(domain.id)}
                    onChange={(_, data) =>
                      handleSelectAllPoliciesForDomain(domain.id, data.checked === true)
                    }
                    data-testid={`matrix-assignment-domain-select-all-checkbox-${domainIndex}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalAssignments > 0 && (
        <div className={styles.summaryText} data-testid="matrix-assignment-summary-text-container">
          <Text data-testid="matrix-assignment-summary-text">
            → {totalAssignments} total assignment{totalAssignments !== 1 ? 's' : ''} across{' '}
            {domainsWithAssignments} domain{domainsWithAssignments !== 1 ? 's' : ''}
          </Text>
        </div>
      )}

      <div className={styles.actions} data-testid="matrix-assignment-actions">
        <Button
          appearance="subtle"
          onClick={onBack}
          icon={<ArrowLeft20Regular data-testid="matrix-assignment-back-icon" />}
          data-testid="matrix-assignment-back"
        >
          Back to Mode Selection
        </Button>

        <Button
          appearance="primary"
          onClick={handleComplete}
          icon={<Checkmark20Regular data-testid="matrix-assignment-review-apply-icon" />}
          iconPosition="after"
          disabled={totalAssignments === 0}
          data-testid="matrix-assignment-review-apply"
        >
          Review & Apply ({totalAssignments})
        </Button>
      </div>
    </div>
  );
};
